import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  FileText,
  FolderKanban,
  PackageCheck,
  Target
} from 'lucide-react'
import { supabase } from '../supabase'
import { useUser } from '../UserContext'

function currencySymbol(c) {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
}

function formatMoney(value, currency = 'GBP') {
  const amount = Number(value || 0)

  if (amount >= 1000000) return `${currencySymbol(currency)}${(amount / 1000000).toFixed(2)}M`
  if (amount >= 1000) return `${currencySymbol(currency)}${Math.round(amount / 1000)}k`

  return `${currencySymbol(currency)}${amount.toLocaleString()}`
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function normaliseDateValue(value) {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return date.toISOString().split('T')[0]
}

function getDeliveryDueDate(delivery) {
  if (delivery.delivery_date) return normaliseDateValue(delivery.delivery_date)

  const customFields = delivery.custom_fields || {}
  const preferredKeys = [
    'due_date',
    'delivery_date',
    'date_of_activation',
    'activation_date',
    'deadline',
    'date'
  ]

  for (const key of preferredKeys) {
    if (customFields[key]) return normaliseDateValue(customFields[key])
  }

  const matchingKey = Object.keys(customFields).find(key =>
    key.toLowerCase().includes('date') ||
    key.toLowerCase().includes('deadline') ||
    key.toLowerCase().includes('due')
  )

  return matchingKey ? normaliseDateValue(customFields[matchingKey]) : null
}

export default function PortfolioDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { orgId, can } = useUser()
  const canViewFinancials = can('financials.view')

  const [portfolio, setPortfolio] = useState(null)
  const [projects, setProjects] = useState([])
  const [allocations, setAllocations] = useState([])
  const [rights, setRights] = useState([])
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchPortfolio()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, orgId])

  async function fetchPortfolio() {
    if (!orgId) return

    setLoading(true)
    setError(null)

    const { data: portfolioData, error: portfolioError } = await supabase
      .from('portfolios')
      .select('*')
      .eq('id', id)
      .eq('org_id', orgId)
      .single()

    if (portfolioError) {
      console.error('Failed to load portfolio:', portfolioError)
      setError(`Failed to load portfolio: ${portfolioError.message}`)
      setLoading(false)
      return
    }

    const { data: projectData, error: projectError } = await supabase
      .from('secure_projects')
      .select('*')
      .eq('portfolio_id', id)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })

    if (projectError) {
      console.error('Failed to load portfolio projects:', projectError)
      setError(`Failed to load portfolio projects: ${projectError.message}`)
      setLoading(false)
      return
    }

    const projectIds = (projectData || []).map(project => project.id)

    if (projectIds.length === 0) {
      setPortfolio(portfolioData)
      setProjects([])
      setAllocations([])
      setRights([])
      setDeliveries([])
      setLoading(false)
      return
    }

    const [
      { data: allocationData, error: allocationError },
      { data: rightsData, error: rightsError }
    ] = await Promise.all([
      supabase
        .from('secure_deal_project_allocations')
        .select('*')
        .eq('org_id', orgId)
        .in('project_id', projectIds),

      supabase
        .from('rights')
        .select('id, project_id, deal_id, name, right_type, quantity, created_at')
        .eq('org_id', orgId)
        .in('project_id', projectIds)
    ])

    if (allocationError) {
      console.error('Failed to load portfolio allocations:', allocationError)
    }

    if (rightsError) {
      console.error('Failed to load portfolio rights:', rightsError)
    }

    const rightIds = (rightsData || []).map(right => right.id)
    let deliveryData = []

    if (rightIds.length > 0) {
      const { data, error: deliveryError } = await supabase
        .from('secure_deliveries')
        .select('id, right_id, name, status, delivery_date, custom_fields, created_at')
        .eq('org_id', orgId)
        .in('right_id', rightIds)

      if (deliveryError) {
        console.error('Failed to load portfolio deliveries:', deliveryError)
      } else {
        deliveryData = data || []
      }
    }

    setPortfolio(portfolioData)
    setProjects(projectData || [])
    setAllocations(allocationData || [])
    setRights(rightsData || [])
    setDeliveries(deliveryData)
    setLoading(false)
  }

  const metrics = useMemo(() => {
    const totalRevenue = allocations.reduce((sum, allocation) => {
      return sum + (Number(allocation.amount) || 0)
    }, 0)

    const totalBudget = projects.reduce((sum, project) => {
      return sum + (Number(project.budget) || 0)
    }, 0)

    const dealMap = new Map()
    allocations.forEach(allocation => {
      if (allocation.deals?.id) dealMap.set(allocation.deals.id, allocation.deals)
    })

    const completedDeliveries = deliveries.filter(delivery =>
      delivery.status === 'delivered' || delivery.status === 'approved'
    ).length

    const completionRate = deliveries.length > 0
      ? Math.round((completedDeliveries / deliveries.length) * 100)
      : 0

    const today = new Date().toISOString().split('T')[0]

    const enrichedDeliveries = deliveries.map(delivery => {
      const right = rights.find(item => item.id === delivery.right_id)
      const project = right ? projects.find(item => item.id === right.project_id) : null

      return {
        ...delivery,
        dueDate: getDeliveryDueDate(delivery),
        right,
        project
      }
    })

    const overdueDeliveries = enrichedDeliveries.filter(delivery =>
      delivery.dueDate &&
      delivery.dueDate < today &&
      delivery.status !== 'delivered' &&
      delivery.status !== 'approved'
    )

    const upcomingDeadlines = [
      ...enrichedDeliveries
        .filter(delivery =>
          delivery.dueDate &&
          delivery.dueDate >= today &&
          delivery.status !== 'delivered' &&
          delivery.status !== 'approved'
        )
        .map(delivery => ({
          date: delivery.dueDate,
          title: delivery.name || delivery.right?.name || 'Delivery',
          subtitle: delivery.project?.name || 'Project',
          type: 'Delivery'
        })),
      ...Array.from(dealMap.values())
        .filter(deal => deal.end_date && deal.end_date >= today)
        .map(deal => ({
          date: deal.end_date,
          title: deal.name,
          subtitle: deal.clients?.name || 'Client',
          type: 'Deal'
        }))
    ]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 6)

    const revenueByProject = projects
      .map(project => ({
        id: project.id,
        name: project.name,
        revenue: allocations
          .filter(allocation => allocation.project_id === project.id)
          .reduce((sum, allocation) => sum + (Number(allocation.amount) || 0), 0),
        budget: Number(project.budget) || 0
      }))
      .sort((a, b) => b.revenue - a.revenue)

    const topClientsMap = {}
    allocations.forEach(allocation => {
      const clientName = allocation.deals?.clients?.name || 'No client'
      topClientsMap[clientName] = (topClientsMap[clientName] || 0) + (Number(allocation.amount) || 0)
    })

    const topClients = Object.entries(topClientsMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)

    const recentActivity = [
      ...projects.map(project => ({
        title: project.name,
        subtitle: 'Project created',
        date: project.created_at,
        path: `/projects/${project.id}`
      })),
      ...allocations.map(allocation => ({
        title: allocation.deals?.name || 'Deal allocation',
        subtitle: `${allocation.deals?.clients?.name || 'Client'} allocated to portfolio`,
        date: allocation.created_at,
        path: allocation.deal_id ? `/deals/${allocation.deal_id}` : '/deals'
      })),
      ...rights.map(right => ({
        title: right.name || 'Right added',
        subtitle: right.right_type || 'Commercial right',
        date: right.created_at,
        path: right.deal_id ? `/deals/${right.deal_id}` : '/rights'
      })),
      ...deliveries.map(delivery => ({
        title: delivery.name || 'Delivery updated',
        subtitle: delivery.status || 'Delivery',
        date: delivery.created_at,
        path: '/deliveries'
      }))
    ]
      .filter(item => item.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 6)

    return {
      totalRevenue,
      totalBudget,
      dealCount: dealMap.size,
      rightCount: rights.length,
      deliveryCount: deliveries.length,
      completedDeliveries,
      outstandingDeliveries: deliveries.length - completedDeliveries,
      overdueDeliveries,
      overdueRights: new Set(overdueDeliveries.map(delivery => delivery.right_id).filter(Boolean)).size,
      upcomingDeadlines,
      completionRate,
      revenueByProject,
      topClients,
      recentActivity,
      budgetUtilisation: totalBudget > 0 ? Math.round((totalRevenue / totalBudget) * 100) : 0
    }
  }, [allocations, deliveries, projects, rights])

  const maxProjectRevenue = Math.max(...metrics.revenueByProject.map(project => project.revenue), 1)
  const maxClientRevenue = Math.max(...metrics.topClients.map(client => client.value), 1)

  if (loading) {
    return (
      <div className="hv-page">
        <div style={{ color: 'var(--hv-text-muted)', fontSize: 13.5, padding: '40px 0' }}>
          Loading portfolio dashboard...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="hv-page">
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/contracts')} style={{ marginBottom: 18 }}>
          <ArrowLeft size={15} />
          Projects
        </button>

        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px'
        }}>
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="hv-page">
      <style>{`
        .portfolio-dashboard-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 24px;
        }

        .portfolio-kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 16px;
        }

        .portfolio-panel-grid {
          display: grid;
          grid-template-columns: 1.35fr 1fr 0.9fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .portfolio-bottom-grid {
          display: grid;
          grid-template-columns: 1.15fr 0.95fr 0.9fr;
          gap: 16px;
        }

        .portfolio-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(15, 21, 35, 0.05);
        }

        .portfolio-kpi {
          padding: 22px;
          min-height: 116px;
        }

        .portfolio-kpi-label {
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.075em;
          color: var(--hv-text-muted);
          margin-bottom: 16px;
        }

        .portfolio-kpi-value {
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -0.8px;
          color: var(--hv-text-primary);
          line-height: 1;
        }

        .portfolio-kpi-sub {
          font-size: 11.5px;
          color: var(--hv-text-muted);
          margin-top: 8px;
        }

        .portfolio-panel {
          padding: 22px;
        }

        .portfolio-panel-title {
          font-size: 14px;
          font-weight: 800;
          color: var(--hv-text-primary);
          margin-bottom: 18px;
        }

        .portfolio-row {
          display: grid;
          grid-template-columns: 150px 1fr 78px;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .portfolio-row-name {
          font-size: 12.5px;
          font-weight: 700;
          color: var(--hv-text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .portfolio-track {
          height: 7px;
          background: #EEF2F7;
          border-radius: 999px;
          overflow: hidden;
        }

        .portfolio-fill {
          height: 100%;
          background: linear-gradient(90deg, #4F46E5, #2563EB);
          border-radius: 999px;
        }

        .portfolio-health-ring {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        .portfolio-health-ring-inner {
          width: 112px;
          height: 112px;
          background: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }

        .portfolio-deadline-row,
        .portfolio-activity-row {
          display: grid;
          grid-template-columns: 42px 1fr auto;
          gap: 12px;
          padding: 11px 0;
          align-items: center;
          border-bottom: 1px solid var(--hv-border-light);
        }

        .portfolio-deadline-row:last-child,
        .portfolio-activity-row:last-child {
          border-bottom: none;
        }

        .portfolio-status-line {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid var(--hv-border-light);
        }

        .portfolio-status-line:last-child {
          border-bottom: none;
        }

        @media (max-width: 1180px) {
          .portfolio-kpi-grid,
          .portfolio-panel-grid,
          .portfolio-bottom-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>

      <div className="portfolio-dashboard-header">
        <div>
          <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/contracts')} style={{ marginBottom: 14 }}>
            <ArrowLeft size={15} />
            Projects
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: 'var(--hv-accent-light)',
              color: 'var(--hv-accent)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FolderKanban size={20} />
            </div>
            <div>
              <h1 className="hv-page-title">{portfolio?.name || 'Portfolio'}</h1>
              <p className="hv-page-subtitle">{portfolio?.description || 'Executive sponsorship portfolio dashboard'}</p>
            </div>
          </div>
        </div>

        <button className="hv-btn hv-btn-primary" onClick={() => navigate('/contracts')}>
          Manage Projects
        </button>
      </div>

      <div className="portfolio-kpi-grid">
        {canViewFinancials && <KpiCard
          label="Total Revenue"
          value={formatMoney(metrics.totalRevenue)}
          sub={`${metrics.dealCount} deals across ${projects.length} projects`}
          icon={<CircleDollarSign size={18} />}
        />}
        {canViewFinancials && <KpiCard
          label="Total Budget"
          value={formatMoney(metrics.totalBudget)}
          sub={metrics.totalBudget > 0 ? `${metrics.budgetUtilisation}% utilised` : 'No project budgets set'}
          icon={<Target size={18} />}
        />}
        <KpiCard
          label="Rights"
          value={metrics.rightCount}
          sub={`${metrics.overdueRights} rights have overdue deliveries`}
          icon={<FileText size={18} />}
          danger={metrics.overdueRights > 0}
        />
        <KpiCard
          label="Deliveries"
          value={`${metrics.completedDeliveries}/${metrics.deliveryCount}`}
          sub={`${metrics.outstandingDeliveries} outstanding · ${metrics.overdueDeliveries.length} overdue`}
          icon={<PackageCheck size={18} />}
          danger={metrics.overdueDeliveries.length > 0}
        />
      </div>

      <div className="portfolio-panel-grid">
        {canViewFinancials && <div className="portfolio-card portfolio-panel">
          <div className="portfolio-panel-title">Revenue by Project</div>

          {metrics.revenueByProject.length === 0 ? (
            <EmptyText>No project revenue yet.</EmptyText>
          ) : (
            metrics.revenueByProject.map(project => {
              const pct = Math.round((project.revenue / maxProjectRevenue) * 100)

              return (
                <button
                  key={project.id}
                  className="portfolio-row"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                >
                  <div className="portfolio-row-name">{project.name}</div>
                  <div className="portfolio-track">
                    <div className="portfolio-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--hv-text-primary)', textAlign: 'right' }}>
                    {formatMoney(project.revenue)}
                  </div>
                </button>
              )
            })
          )}
        </div>}

        <div className="portfolio-card portfolio-panel">
          <div className="portfolio-panel-title">Delivery Health</div>

          <div className="portfolio-health-ring" style={{
            background: `conic-gradient(#4F46E5 ${metrics.completionRate * 3.6}deg, #EEF2F7 0deg)`
          }}>
            <div className="portfolio-health-ring-inner">
              <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-1px' }}>{metrics.completionRate}%</div>
              <div style={{ fontSize: 12, color: 'var(--hv-text-muted)' }}>Complete</div>
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            <StatusLine icon={<CheckCircle2 size={15} />} label="Complete" value={metrics.completedDeliveries} color="#16A34A" />
            <StatusLine icon={<CalendarDays size={15} />} label="Outstanding" value={metrics.outstandingDeliveries} />
            <StatusLine icon={<AlertCircle size={15} />} label="Overdue" value={metrics.overdueDeliveries.length} color="#EF4444" />
          </div>
        </div>

        {canViewFinancials && <div className="portfolio-card portfolio-panel">
          <div className="portfolio-panel-title">Budget Utilisation</div>

          <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-1px', color: 'var(--hv-text-primary)', marginBottom: 8 }}>
            {metrics.totalBudget > 0 ? `${metrics.budgetUtilisation}%` : '—'}
          </div>
          <div style={{ color: 'var(--hv-text-muted)', fontSize: 12.5, marginBottom: 18 }}>
            {formatMoney(metrics.totalRevenue)} revenue against {formatMoney(metrics.totalBudget)} budget.
          </div>

          <div className="portfolio-track" style={{ height: 10 }}>
            <div
              className="portfolio-fill"
              style={{ width: `${Math.min(metrics.budgetUtilisation, 100)}%`, background: metrics.budgetUtilisation > 100 ? '#EF4444' : '#16A34A' }}
            />
          </div>
        </div>}
      </div>

      <div className="portfolio-bottom-grid">
        <div className="portfolio-card portfolio-panel">
          <div className="portfolio-panel-title">Upcoming Deadlines</div>

          {metrics.upcomingDeadlines.length === 0 ? (
            <EmptyText>No upcoming deadlines.</EmptyText>
          ) : (
            metrics.upcomingDeadlines.map((item, index) => (
              <div key={`${item.title}-${index}`} className="portfolio-deadline-row">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{new Date(item.date).getDate()}</div>
                  <div style={{ fontSize: 10, color: 'var(--hv-text-muted)', textTransform: 'uppercase' }}>
                    {new Date(item.date).toLocaleDateString('en-GB', { month: 'short' })}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 800 }}>{item.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--hv-text-muted)' }}>{item.subtitle}</div>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--hv-text-muted)' }}>
                  {item.type}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="portfolio-card portfolio-panel">
          <div className="portfolio-panel-title">Top Clients</div>

          {metrics.topClients.length === 0 ? (
            <EmptyText>No client revenue yet.</EmptyText>
          ) : (
            metrics.topClients.map(client => {
              const pct = Math.round((client.value / maxClientRevenue) * 100)

              return (
                <div key={client.name} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 7 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--hv-text-primary)' }}>{client.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>{formatMoney(client.value)}</div>
                  </div>
                  <div className="portfolio-track">
                    <div className="portfolio-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="portfolio-card portfolio-panel">
          <div className="portfolio-panel-title">Recent Activity</div>

          {metrics.recentActivity.length === 0 ? (
            <EmptyText>No recent activity yet.</EmptyText>
          ) : (
            metrics.recentActivity.map((item, index) => (
              <button
                key={`${item.title}-${index}`}
                className="portfolio-activity-row"
                onClick={() => navigate(item.path)}
                style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: 'var(--hv-accent-light)',
                  color: 'var(--hv-accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 800
                }}>
                  {index + 1}
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 800 }}>{item.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--hv-text-muted)' }}>{item.subtitle}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--hv-text-muted)' }}>{formatDate(item.date)}</div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function KpiCard({ label, value, sub, icon, danger = false }) {
  return (
    <div className="portfolio-card portfolio-kpi">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div className="portfolio-kpi-label">{label}</div>
          <div className="portfolio-kpi-value" style={{ color: danger ? '#DC2626' : 'var(--hv-text-primary)' }}>
            {value}
          </div>
          <div className="portfolio-kpi-sub" style={{ color: danger ? '#DC2626' : 'var(--hv-text-muted)' }}>
            {sub}
          </div>
        </div>
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: danger ? 'var(--hv-red-bg)' : 'var(--hv-accent-light)',
          color: danger ? 'var(--hv-red)' : 'var(--hv-accent)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {icon}
        </div>
      </div>
    </div>
  )
}

function StatusLine({ icon, label, value, color = 'var(--hv-text-primary)' }) {
  return (
    <div className="portfolio-status-line">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hv-text-secondary)' }}>
        {icon}
        <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
      </div>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
    </div>
  )
}

function EmptyText({ children }) {
  return (
    <div style={{ color: 'var(--hv-text-muted)', fontSize: 13 }}>
      {children}
    </div>
  )
}
